<template>
  <v-form id="appointment-form" ref="appointmentForm" class="d-flex flex-column gap-6 flex-grow-1">
    <!--
      In mobile devices the touch event happens instead of the hover event,
      so the hover state is not being reset when the user navigates back to a previous step.
      Ideally we should be able to use @media (hover: hover) {} to solve this, but that would
      require use to re-write the Vuetify theme, and that's not something we can do right now.
    -->
    <v-btn
      :key="currentStep"
      variant="text"
      prepend-icon="mdi-arrow-left"
      style="align-self: flex-start"
      @click="handleBack">
      Back
    </v-btn>

    <div
      class="d-flex flex-column gap-6 px-4 flex-grow-1"
      style="
        padding-bottom: calc(
          72px + 16px
        ); /* Enought space so, in case of overflow, the fixed footer does not overlap the last form field */
      ">
      <template v-if="currentStep === 1">
        <div class="checkin__form-container">
          <div class="checkin__form-header">
            <h2 class="typography-heading-md-bold">What's the purpose of your stop?</h2>
          </div>

          <div class="d-flex flex-column gap-8">
            <div
              v-for="group in assetVisitTypesGrouped"
              :key="group.label"
              class="d-flex flex-column gap-3">
              <div class="typography-body-md-bold">{{ group.label }}</div>
              <div class="d-flex flex-column gap-2">
                <v-card
                  v-for="option in group.options"
                  :key="option.key"
                  class="option-card"
                  :class="{ 'option-card--selected': selectedOptionKey === option.key }"
                  @click="selectOption(option)"
                  variant="outlined"
                  elevation="1">
                  <v-card-text class="d-flex align-center gap-3">
                    <div class="d-flex align-center justify-center icon-option-container">
                      <template v-for="(icon, index) in option.icons" :key="index">
                        <v-icon>{{ icon }}</v-icon>
                      </template>
                    </div>
                    <div class="flex-grow-1 typography-body-sm-bold">{{ option.label }}</div>
                    <v-icon size="16px">mdi-arrow-right</v-icon>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="currentStep === 2">
        <asset-details-form
          v-if="isYMSEnabled"
          v-model:assetContainerData="assetContainerData"
          :asset-visit-type="assetVisitType"
          :asset-visit-types="getAssetVisitTypesGrouped"
          :should-show-asset-details-form="shouldShowAssetDetailsForm"
          :asset-container-types="assetContainerTypes"
          :max-container-data="MAX_CONTAINER_DATA" />

        <div class="checkin__form-container">
          <v-alert
            v-if="inlineError.msg"
            dense
            outlined
            type="error"
            :icon="inlineError.icon ?? 'mdi-alert-circle-outline'">
            {{ inlineError.msg }}
          </v-alert>

          <v-alert
            v-if="inlineWarning.msg"
            dense
            outlined
            type="warning"
            :icon="inlineWarning.icon ?? 'mdi-alert-circle-outline'">
            {{ inlineWarning.msg }}
          </v-alert>

          <div class="checkin__form-header">
            <h2 class="typography-heading-md-bold">Appointment information</h2>
            <h3 class="typography-body-sm text__color-text-tertiary">
              Make sure to fill in this field if you have an appointment.
            </h3>
          </div>
          <div class="checkin__form-body">
            <div class="custom-field">
              <label class="custom-field__label" v-if="appointmentIdentifierLabel">
                {{ appointmentIdentifierLabel }}
              </label>
              <v-text-field
                :type="referenceNumberInputMode === 'numeric' ? 'number' : 'text'"
                :inputmode="referenceNumberInputMode === 'numeric' ? 'numeric' : 'text'"
                @keydown="onRefNumKeyDown"
                color="primary"
                variant="outlined"
                data-testid="comet-appointment-identifier"
                v-model="identifier"
                @focus="scrollIntoView"
                hide-details></v-text-field>
            </div>
          </div>
        </div>

        <div class="checkin__form-container">
          <div class="checkin__form-header">
            <h2 class="typography-heading-md-bold">Check-in Details</h2>
            <h3 class="typography-body-sm text__color-text-tertiary">
              Additional information about your visit.
            </h3>
          </div>

          <div class="checkin__form-body">
            <!-- Company Selector -->
            <company-select
              :warehouse-id="warehouse.id"
              @update:modelValue="handleCompanySelected" />

            <key-value-list>
              <key-value>
                <template #label>
                  <span class="typography-body-sm-bold">
                    Driver's Phone Number
                    <span class="text-red">*</span>
                  </span>
                </template>
                <template #value>
                  <!-- TODO: use the global validator for the required rule -->
                  <phone-number-field-v2
                    v-model="phoneNumber"
                    :default-country="defaultCountry"
                    :input-props="{
                      'data-testid': 'driver-phone-number-field'
                    }"
                    :rules="[(value: string, phone: any, messageOptions?: { example?: string }) => {
                    if (typeof value !== 'string' || value.trim().length === 0) {
                      return 'This field is required';
                    }

                    const { example } = messageOptions ?? {};
                    return phone?.valid || `This is not a valid phone number${example ? ` (example: ${example})` : ''}`;
                    }]" />

                  <div class="d-flex flex-column gap-2">
                    <v-radio-group inline hide-details v-model="messagingType" color="primary">
                      <v-radio value="SMS" :disabled="!isSMSMessagingEnabled">
                        <template #label>
                          <span class="typography-body-sm">SMS</span>
                        </template>
                      </v-radio>
                      <v-radio value="WhatsApp" :disabled="!isWhatsAppMessagingEnabled">
                        <template #label>
                          <span class="typography-body-sm">WhatsApp</span>
                        </template>
                      </v-radio>
                    </v-radio-group>

                    <v-banner v-if="useWhatsApp" class="whatsapp-banner">
                      <v-icon size="24">mdi-whatsapp</v-icon>
                      <div class="d-flex flex-column gap-5">
                        <div class="d-flex flex-column gap-1">
                          <div class="typography-body-md-bold">Activate WhatsApp Messaging</div>
                          <div class="typography-body-md">
                            To chat with the warehouse, please reply "OK" when we send you the first
                            WhatsApp message.
                          </div>
                        </div>
                        <div class="typography-body-md-bold">
                          The first WhatsApp message may take up to one minute to arrive.
                        </div>
                      </div>
                    </v-banner>
                  </div>
                </template>
              </key-value>
            </key-value-list>

            <!-- Fixed asset visit fields -->
            <custom-fields :warehouse="warehouse" :value="assetDetailsFields" />

            <!-- Custom Forms -->
            <custom-fields
              :warehouse="warehouse"
              v-if="additionalAssetDetailsFieldsExist"
              :value="additionalAssetDetailsFields" />

            <!-- SmartGate fields -->
            <license-plate-select
              v-if="isSmartGateIntegrationEnabled && showLicensePlateSmartGateField"
              :warehouse-id="warehouse.id"
              @update:modelValue="handleLicensePlateSelected"
              :rules="licensePlateRules" />

            <dot-select
              v-if="isSmartGateIntegrationEnabled && showDotNumberSmartGateField"
              :warehouse-id="warehouse.id"
              @update:modelValue="handleDotSelected"
              :rules="dotNumberRules" />

            <!-- These notes will be appended to the appointment.notes -->
            <div class="custom-field">
              <label class="custom-field__label">Notes</label>
              <v-textarea
                color="primary"
                variant="outlined"
                data-testid="comet-appointment-notes"
                v-model="notes"
                placeholder="Any special instructions or notes to the facility?"
                maxlength="4096"
                counter="4096"
                hide-details="auto"
                clearable />
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="checkin__button-container">
      <v-btn
        v-if="currentStep === 2"
        block
        prepend-icon="mdi-check"
        :color="!isStepValid ? 'none' : 'primary'"
        :disabled="!isStepValid"
        :loading="submitting"
        @click="submit">
        Complete check-in
      </v-btn>
    </div>
  </v-form>
</template>

<script lang="ts">
import {
  AssetVisitType,
  AssetContainerType,
  WarehouseFeatureEnum,
  isFeatureEnabledForWarehouse,
  ReferenceNumberInputModes,
  EntitySettings,
  ObservationMatchAttribute,
  AssetVisitDirection
} from '@nova/core';
import { defineComponent, ref } from 'vue';
import type { VForm } from 'vuetify/components';
import { get, trim } from 'lodash';
import axios from 'axios';
import type { PropType } from 'vue';
import { isString, isUUID } from 'class-validator';

import {
  clearLocalStorageKey,
  updateObjectInLocalStorage
} from '@/components/mixins/localStorageMixin';
import { KeyValueList, KeyValue } from '@satellite/components/elements/key_value';
import { useApiErrors, useCheckinForms } from '@/components/composables';
import { getSenderPhoneNumber } from '@/config';
import AssetDetailsForm from './AssetDetailsForm.vue';
import PhoneNumberFieldV2 from '@satellite/components/elements/forms/PhoneNumberFieldV2.vue';

import type { IWarehouse, IAppointment } from '@nova/core';

const MAX_CONTAINER_DATA = 3;

type MessagingType = 'SMS' | 'WhatsApp';

export default defineComponent({
  name: 'AppointmentForm',
  components: {
    AssetDetailsForm,
    PhoneNumberFieldV2,
    KeyValueList,
    KeyValue
  },
  props: {
    latitude: {
      type: String,
      required: true
    },
    longitude: {
      type: String,
      required: true
    },
    warehouse: {
      type: Object as PropType<IWarehouse>,
      required: true
    }
  },
  emits: ['back'],
  setup(props) {
    const { makeErrorScreenParams } = useApiErrors();
    const {
      assetDetailsFields,
      additionalAssetDetailsFields,
      additionalAssetDetailsFieldsExist,
      additionalAssetDetailsTriggers,
      areRequiredAssetDetailsFilled,
      setRequiredAssetVisitFields,
      extractFieldsFromTriggers,
      getAssetDetailsData,
      prepareCustomFormsData
    } = useCheckinForms(props.warehouse);

    const currentStep = ref(
      isFeatureEnabledForWarehouse(props.warehouse, WarehouseFeatureEnum.YMS) ? 1 : 2
    );
    const isStepValid = ref(false);
    const assetVisitType = ref(AssetVisitType.Live);
    const assetVisitTypeIcon = ref<string[]>([]);
    const assetContainerData = ref([
      { containerCode: '', containerType: AssetContainerType.Trailer }
    ]);

    const assetVisitTypesGrouped = ref([
      {
        group: AssetVisitDirection.Inbound,
        label: 'Inbound',
        options: [
          {
            key: 'unload',
            value: AssetVisitType.LiveUnload,
            direction: AssetVisitDirection.Inbound,
            label: 'Unload my truck',
            icons: ['mdi-arrow-left-thin', 'mdi-truck-outline']
          },
          {
            key: 'drop',
            value: AssetVisitType.Drop,
            direction: AssetVisitDirection.Inbound,
            label: 'Drop my trailer or container',
            icons: ['mdi-hook-off']
          }
        ]
      },
      {
        group: AssetVisitDirection.Outbound,
        label: 'Outbound',
        options: [
          {
            key: 'load',
            value: AssetVisitType.LiveLoad,
            direction: AssetVisitDirection.Outbound,
            label: 'Load my truck',
            icons: ['mdi-arrow-right-thin', 'mdi-truck-outline']
          },
          {
            key: 'pickup',
            value: AssetVisitType.PickUp,
            direction: AssetVisitDirection.Outbound,
            label: 'Hook a trailer or container',
            icons: ['mdi-hook']
          }
        ]
      },
      {
        group: AssetVisitDirection.InboundAndOutbound,
        label: 'Inbound & Outbound',
        options: [
          {
            key: 'unload-load',
            value: AssetVisitType.Live,
            direction: AssetVisitDirection.InboundAndOutbound,
            label: 'Unload & Load my truck',
            icons: ['mdi-swap-horizontal', 'mdi-truck-outline']
          },
          {
            key: 'drop-hook',
            value: AssetVisitType.DropHook,
            direction: AssetVisitDirection.InboundAndOutbound,
            label: 'Drop & Hook a trailer or container',
            icons: ['mdi-hook-off', 'mdi-hook']
          }
        ]
      }
    ]);

    const selectedOptionKey = ref('unload');

    const handleNext = () => {
      currentStep.value++;
    };

    function selectOption(option: {
      key: string;
      value: AssetVisitType;
      direction: AssetVisitDirection;
      icons: Array<string>;
    }) {
      selectedOptionKey.value = option.key;
      assetVisitType.value = option.value;
      handleNext();
    }

    const defaultCountry = ref('US');
    const messagingType = ref<MessagingType>('SMS');
    const isSMSMessagingEnabled = ref(false);
    const isWhatsAppMessagingEnabled = ref(false);

    const updateMessagingSettings = (warehouse: IWarehouse) => {
      const country = get(warehouse, 'country') || 'USA';
      defaultCountry.value = country;
      messagingType.value = ['USA', 'CAN'].includes(country) ? 'SMS' : 'WhatsApp';
      isSMSMessagingEnabled.value = messagingType.value === 'SMS';
      isWhatsAppMessagingEnabled.value = messagingType.value === 'WhatsApp';
    };

    // Initialize with current warehouse
    updateMessagingSettings(props.warehouse);

    const assetContainerTypes = [
      { title: 'Trailer', value: AssetContainerType.Trailer },
      { title: 'Container', value: AssetContainerType.Container },
      { title: 'Oversized', value: AssetContainerType.Oversized },
      { title: 'Other', value: AssetContainerType.Other }
    ];

    const requiredValidator = (value: string, label: string) =>
      !value || value?.length === 0 ? `${label} is required` : true;

    return {
      additionalAssetDetailsFields,
      additionalAssetDetailsFieldsExist,
      additionalAssetDetailsTriggers,
      areRequiredAssetDetailsFilled,
      assetContainerData,
      AssetContainerType,
      AssetVisitType,
      assetContainerTypes,
      assetDetailsFields,
      assetVisitType,
      assetVisitTypesGrouped,
      currentStep,
      defaultCountry,
      messagingType,
      isSMSMessagingEnabled,
      isWhatsAppMessagingEnabled,
      isStepValid,
      MAX_CONTAINER_DATA,
      requiredValidator,
      selectedOptionKey,
      extractFieldsFromTriggers,
      getAssetDetailsData,
      makeErrorScreenParams,
      prepareCustomFormsData,
      selectOption,
      setRequiredAssetVisitFields,
      updateMessagingSettings
    };
  },
  data() {
    return {
      submitting: false,
      notes: '',
      phoneNumber: '',
      identifier: '',
      companyId: null as string | null,
      companyName: null as string | null,
      licensePlate: null as string | null,
      dotNumber: null as string | null,
      appointment: {} as IAppointment,
      inlineError: {
        msg: '',
        icon: ''
      },
      inlineWarning: {
        msg: '',
        icon: ''
      }
    };
  },
  computed: {
    appointmentIdentifierLabel() {
      const referenceDisplayName =
        this.warehouse.settings?.referenceNumberDisplayName ?? 'PO Number';
      return `${referenceDisplayName} or Confirmation Number`;
    },
    referenceNumberInputMode(): ReferenceNumberInputModes {
      const setting =
        this.warehouse.settings?.referenceNumberInputMode ||
        EntitySettings.Org.referenceNumberInputMode.defaultValue;
      return setting as ReferenceNumberInputModes;
    },
    useWhatsApp() {
      return this.messagingType === 'WhatsApp';
    },
    isYMSEnabled() {
      return isFeatureEnabledForWarehouse(this.warehouse, WarehouseFeatureEnum.YMS);
    },
    isSmartGateIntegrationEnabled() {
      return isFeatureEnabledForWarehouse(this.warehouse, WarehouseFeatureEnum.SmartGate);
    },
    observationMatchAttribute() {
      return this.warehouse.settings?.observationEventMatchAttribute;
    },
    showAnySmartGateFields() {
      return this.observationMatchAttribute === ObservationMatchAttribute.ANY;
    },
    showLicensePlateSmartGateField() {
      return (
        this.observationMatchAttribute === ObservationMatchAttribute.LICENSE_PLATE ||
        this.showAnySmartGateFields
      );
    },
    showDotNumberSmartGateField() {
      return (
        this.observationMatchAttribute === ObservationMatchAttribute.DOT_NUMBER ||
        this.showAnySmartGateFields
      );
    },
    getAssetVisitTypesGrouped() {
      return this.assetVisitTypesGrouped
        .flatMap(group => group.options)
        .map(option => ({
          value: option.value,
          label: option.label,
          direction: option.direction,
          icons: option.icons
        }));
    },
    licensePlateRules() {
      if (this.observationMatchAttribute === ObservationMatchAttribute.ANY) {
        return [
          (value: { id: string | null; name: string } | null) => {
            const licensePlateValue = value?.name?.trim() || '';
            const dotNumberValue = this.dotNumber?.trim() || '';
            if (!licensePlateValue && !dotNumberValue) {
              return 'Please fill in License Plate or DOT Number';
            }
            return true;
          }
        ];
      } else if (this.observationMatchAttribute === ObservationMatchAttribute.LICENSE_PLATE) {
        return [
          (value: { id: string | null; name: string } | null) => {
            if (!value || typeof value.name !== 'string' || value.name.trim().length === 0) {
              return 'This field is required';
            }
            return true;
          }
        ];
      }
      // No validation needed for other cases
      return [];
    },
    dotNumberRules() {
      if (this.observationMatchAttribute === ObservationMatchAttribute.ANY) {
        // When ANY, at least one field (license plate OR dot number) must be filled
        return [
          (value: { id: string | null; name: string } | null) => {
            const dotNumberValue = value?.name?.trim() || '';
            const licensePlateValue = this.licensePlate?.trim() || '';
            if (!dotNumberValue && !licensePlateValue) {
              return 'Please fill in License Plate or DOT Number';
            }
            return true;
          }
        ];
      } else if (this.observationMatchAttribute === ObservationMatchAttribute.DOT_NUMBER) {
        // When DOT_NUMBER, this field is required
        return [
          (value: { id: string | null; name: string } | null) => {
            if (!value || typeof value.name !== 'string' || value.name.trim().length === 0) {
              return 'This field is required';
            }
            return true;
          }
        ];
      }
      // No validation needed for other cases
      return [];
    },
    shouldShowAssetDetailsForm() {
      // Hide asset details form when the visit type is "Hook a trailer or container" (PickUp)
      return this.assetVisitType !== AssetVisitType.PickUp;
    }
  },
  methods: {
    onRefNumKeyDown(event: KeyboardEvent) {
      // Firefox, unlike Chrome, does not prevent letters from being typed in number input
      if (this.referenceNumberInputMode === 'numeric') {
        const key = event.key;
        // eslint-disable-next-line no-useless-escape
        const isLetterOrSymbolKey = /^[a-zA-Z+*&^@#;()%<>!~\-\[\]{}=\s]$/.test(key);
        if (isLetterOrSymbolKey) {
          event.preventDefault();
        }
      }
    },
    scrollIntoView($e: FocusEvent) {
      const targetElement = $e.target as Element;
      targetElement?.scrollIntoView({ behavior: 'smooth' });
    },
    setCustomFields() {
      this.additionalAssetDetailsFields = this.extractFieldsFromTriggers(
        this.additionalAssetDetailsTriggers
      );
    },
    setFixedFields() {
      this.companyId = null;
      this.companyName = null;
      this.phoneNumber = '';
    },
    handleBack() {
      // Goes back to the initial page
      if (!this.isYMSEnabled) {
        this.$emit('back');
        return;
      }

      if (this.currentStep <= 1) {
        this.$emit('back');
      } else {
        this.currentStep--;
      }
    },
    handleCompanySelected(value: { id?: string; name: string }) {
      // reset on each change to avoid stale values
      this.companyId = null;
      this.companyName = null;

      if (!value || !value?.name) {
        return;
      }

      if (isString(value)) {
        const name = value.trim();
        this.companyName = name.length ? name : null;
        return;
      }

      const { id, name } = value;

      if (isString(id) && isUUID(id)) {
        this.companyId = id;
      }
      this.companyName = (name ?? '').trim() || null;
    },
    handleLicensePlateSelected(value: { id?: string; name: string }) {
      // reset on each change to avoid stale values
      this.licensePlate = (value?.name ?? '').trim() || null;
    },
    handleDotSelected(value: { id?: string; name: string }) {
      // reset on each change to avoid stale values
      this.dotNumber = (value?.name ?? '').trim() || null;
    },
    getMessagingTypeSpecificFailedMessage() {
      const senderPhoneNumber = getSenderPhoneNumber();

      if (this.useWhatsApp) {
        return {
          errorTitle: 'Unable to send WhatsApp message',
          errorMessage: `We couldn't send a message to ${
            this.phoneNumber || 'the provided phone number'
          }. <br /><br />
                 If you have previously unsubscribed, please text START to
                 <a href='https://wa.me/${senderPhoneNumber}' target='_blank' rel="noopener noreferrer">${senderPhoneNumber}</a> to start receiving
                 messages from this warehouse.`
        };
      }

      return {
        errorTitle: 'Unable to send SMS message',
        errorMessage: `We couldn't send a message to ${
          this.phoneNumber || 'the provided phone number'
        }. <br /><br />
                 If you have previously unsubscribed, please text START to
                 <a href='sms:${senderPhoneNumber}'>${senderPhoneNumber}</a> to start receiving
                 SMS messages from this warehouse.`
      };
    },
    storeMiscData() {
      /**
       * We use these values in the Complete page to compose the correct instructions.
       */
      updateObjectInLocalStorage('comet:misc', {
        isPlanned: Boolean(this.appointment?.id),
        useWhatsApp: this.useWhatsApp
      });
    },
    async submit() {
      this.inlineError.msg = '';
      this.inlineWarning.msg = '';

      this.submitting = true;

      const warehouseId = this.warehouse?.id || 'N/A';
      const warehouseName = this.warehouse?.name || 'N/A';
      const orgId = this.warehouse?.orgId || 'N/A';
      const orgName = this.warehouse?.org?.name || 'N/A';

      try {
        if (this.identifier) {
          await this.checkApptExists();
        }

        const assetDetails = this.getAssetDetailsData();
        const appointmentId = this.appointment?.id;
        const notes = this.notes?.replace(/\n/g, '<br />') || '';

        this.storeMiscData();

        const smartGateFields = {} as Record<string, unknown>;
        if (this.isSmartGateIntegrationEnabled) {
          smartGateFields.licensePlate = this.licensePlate;
          smartGateFields.dotNumber = this.dotNumber;
        }

        const payload = {
          latitude: this.latitude,
          longitude: this.longitude,
          appointmentId: appointmentId || null,
          assetDetails: {
            ...assetDetails,
            assetVisitType: this.assetVisitType,
            companyId: this.companyId,
            companyHint: this.companyName,
            driverNotes: notes,
            driverAppointmentIdentifier: this.identifier,
            phone: this.phoneNumber,
            useWhatsApp: this.useWhatsApp,
            ...smartGateFields
          },
          customFormsData: this.prepareCustomFormsData(),
          warehouseId: this.warehouse.id,
          assetContainerDetails: this.assetContainerData,
          notes
        };

        let data = null;
        if (appointmentId) {
          data = await this.submitPlanned(payload);
        } else {
          data = await this.submitUnplanned(payload);
        }

        if (data) {
          const assetVisit = get(data, 'appointment.assetVisit') ?? get(data, 'assetVisit');
          const appointment = get(data, 'appointment');
          const customFormsData = get(data, 'customFormsData') ?? [];
          const firstCustomFormTrigger = get(data, 'customFormsData.0.trigger');

          this.mixpanel.track('Completed: Self Check-in', {
            'Appointment ID': appointmentId || 'N/A',
            'Warehouse ID': warehouseId,
            'Warehouse Name': warehouseName,
            'Org ID': orgId,
            'Org Name': orgName,
            'Appointment Validation #':
              appointment?.refNumber || appointment?.confirmationNumber || 'N/A',
            'Asset Visit Type': assetVisit?.visitType || 'N/A',
            'Asset Visit ID': assetVisit?.id || 'N/A',
            'Entry Point': 'Self Check-in',
            'Check-in Type': appointmentId ? 'Planned Arrival' : 'Unplanned Arrival'
          });

          if (Array.isArray(customFormsData) && customFormsData.length > 0) {
            this.mixpanel.track('Submitted: Custom Form', {
              'Custom Form Application': firstCustomFormTrigger?.app,
              'Custom Form Type': firstCustomFormTrigger?.feature,
              'Custom Form Trigger': firstCustomFormTrigger?.category,
              'Appointment ID': appointmentId || 'N/A',
              'Warehouse ID': warehouseId,
              'Warehouse Name': warehouseName,
              'Org ID': orgId,
              'Org Name': orgName,
              'Entry Point': 'Self Check-in'
            });
          }
        }
        this.$router.push({
          name: 'completed',
          params: data.initialMessageFailed ? this.getMessagingTypeSpecificFailedMessage() : {}
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const errorParams = this.makeErrorScreenParams(err);

          this.mixpanel.track('Error: Self Check-in', {
            'Appointment Validation #':
              this.appointment?.refNumber ||
              this.appointment?.confirmationNumber ||
              this.identifier ||
              'N/A',
            'Warehouse ID': warehouseId,
            'Warehouse Name': warehouseName,
            'Org ID': orgId,
            'Org Name': orgName,
            Latitude: this.latitude || 'N/A',
            Longitude: this.longitude || 'N/A',
            'Error Type': errorParams.title,
            'Check-in Type': this.appointment?.id ? 'Planned Arrival' : 'Unplanned Arrival'
          });

          if (errorParams.showErrorInline !== '1') {
            this.$router.push({
              name: 'appointment-error',
              params: errorParams
            });
          } else {
            this.inlineError.msg = errorParams.title;
            this.inlineError.icon = errorParams.icon;
          }
        }
      } finally {
        this.submitting = false;
      }
    },
    async checkApptExists() {
      try {
        const { data } = await this.axiosClient.get(
          `/checkin/public/${encodeURIComponent(this.identifier)}/${this.warehouse.id}/?latitude=${
            this.latitude
          }&longitude=${this.longitude}`
        );

        this.appointment = { ...data.data };
        updateObjectInLocalStorage('comet:appointment', this.appointment);
      } catch (error) {
        this.appointment = {} as IAppointment;
        clearLocalStorageKey('comet:appointment');

        const status = get(error, 'response.status');

        /**
         * If the appointment is not found (error 404), we want to continue with the
         * submit process, so we handle it as unplanned.
         * However, if the appointment is not valid for whatever reason (error 400), we want
         * to throw the error so the user can see the error message.
         */

        if (status !== 404) {
          throw error;
        }
      }
    },
    async submitUnplanned(payload: Record<string, unknown>) {
      const { data } = await this.axiosClient.put(
        `/checkin/public/${this.warehouse.id}/unplanned`,
        payload
      );

      return data;
    },
    async submitPlanned(payload: Record<string, unknown>) {
      const { data } = await this.axiosClient.put(`/checkin/public/${this.warehouse.id}`, payload);

      return data;
    },
    async validateStep() {
      if (this.currentStep === 1) {
        this.isStepValid = true;
      } else if (this.currentStep === 2) {
        // Validate asset container data if YMS is enabled and asset details form is shown
        let isAssetContainerDataValid = true;
        if (this.isYMSEnabled && this.shouldShowAssetDetailsForm) {
          isAssetContainerDataValid = this.assetContainerData.every(
            ({ containerCode, containerType }) =>
              trim(containerCode).length > 0 && trim(containerType).length > 0
          );
        }

        this.isStepValid = isAssetContainerDataValid && this.areRequiredAssetDetailsFilled;

        /**
         * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
         * TODO: when we get rid of the YMS and SmartGate integration feature flags
         * we can change the validation to be run via form ((this.$refs.appointmentForm as VForm).validate())
         * instead of manually validating each field.
         */

        // Validate YMS fields
        if (this.$refs.appointmentForm) {
          const validateResult = await (this.$refs.appointmentForm as VForm).validate();

          this.isStepValid = this.isStepValid && Boolean(validateResult?.valid);
        }

        const isCompanySelected = Boolean(this.companyId || this.companyName);
        this.isStepValid = this.isStepValid && isCompanySelected;

        // Validate SmartGate fields
        if (this.isSmartGateIntegrationEnabled) {
          const areSmartGateFieldsFilled = this.licensePlate || this.dotNumber;

          switch (this.observationMatchAttribute) {
            case ObservationMatchAttribute.ANY:
              this.isStepValid = this.isStepValid && Boolean(areSmartGateFieldsFilled);
              break;
            case ObservationMatchAttribute.LICENSE_PLATE:
              this.isStepValid = this.isStepValid && Boolean(this.licensePlate);
              break;
            case ObservationMatchAttribute.DOT_NUMBER:
              this.isStepValid = this.isStepValid && Boolean(this.dotNumber);
              break;
            default:
              this.inlineWarning.msg = '';
              this.inlineWarning.icon = '';
              break;
          }
        }
      } else {
        this.isStepValid = false;
      }
    }
  },
  mounted() {
    this.setCustomFields();
    this.setRequiredAssetVisitFields();
    this.setFixedFields();
  },
  watch: {
    warehouse() {
      this.setCustomFields();
      this.setRequiredAssetVisitFields();
      this.setFixedFields();
      this.updateMessagingSettings(this.warehouse);
    },
    currentStep: {
      immediate: true,
      handler() {
        this.validateStep();

        this.$nextTick(() => {
          const appointmentForm = document.getElementById('appointment-form');
          if (!appointmentForm) {
            return;
          }

          /**
           * This is a nice-to-have just to improve the UX when the user is on step 2,
           * by focusing the first input of the form.
           */

          if (this.currentStep === 2) {
            // If asset details form is shown, focus the container code input
            // Otherwise, focus the first input of the appointment form
            if (this.isYMSEnabled && this.shouldShowAssetDetailsForm) {
              const firstInputWrapper = appointmentForm.querySelector(
                '[data-testid="asset-visit-form-container-code-0"]'
              );
              const firstInput = firstInputWrapper?.querySelector('input');

              if (firstInput) {
                firstInput.focus();
              }
            } else {
              const firstInput = appointmentForm.querySelector('input');

              if (firstInput) {
                firstInput.focus();
              }
            }
          }

          appointmentForm.scrollIntoView({ behavior: 'smooth' });
        });
      }
    },
    assetContainerData: {
      deep: true,
      handler() {
        this.validateStep();
      }
    },
    notes: {
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    identifier: {
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    assetDetailsFields: {
      deep: true,
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    additionalAssetDetailsFields: {
      deep: true,
      handler() {
        if (this.currentStep === 2) {
          this.validateStep();
        }
      }
    },
    areRequiredAssetDetailsFilled() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    areRequiredCheckinFieldsFilled() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    companyId() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    companyName() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    phoneNumber() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    licensePlate() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    },
    dotNumber() {
      if (this.currentStep === 2) {
        this.validateStep();
      }
    }
  }
});
</script>

<style lang="scss">
.option-card {
  border-radius: 8px;
  border: 1px solid $color-neutral-40 !important;
  cursor: pointer;
}
.option-card--selected {
  border-color: #005a87;
  border-width: 2px;
}

.checkin__form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.checkin__form-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.checkin__form-body {
  display: flex;
  flex-direction: column;
  gap: 16px;

  border-radius: 4px;
  border: 1px solid #ebebeb;
  background-color: #fff;

  padding: 16px;
}

.checkin__button-container {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  background: white;
  border-top: 1px solid #ebebeb;
  padding: $spacing-4;
}

.whatsapp-banner {
  background-color: $color-warning-20 !important;
  color: $color-text-primary !important;
  padding: $spacing-6 !important;

  .v-banner__content {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: $spacing-4;
  }
}

.icon-option-container {
  width: 42px;
  min-width: 42px;
}
</style>
